import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { Header, Loader } from "../../components";
import { MENU_CONFIG, UTILITARIAS_BASE, getIpsosItem } from "../../constants/menuConfig";
import { ADMIN_IDS, DEMO_SWITCH_IDS, REGION_IDS } from "../../constants/permisos";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import { client, getClientes, getOportunidades, logout } from "../../utils";
import { getAllCotizacionesPais, getUsuariosConAgencia } from "../../utils/cotizacionesGuardadas";
import { computePulsoRegional } from "../../utils/pulsoRegional";
import { client as supabase } from "../../utils/supabase";
import styles from "./stylesAdmin.module.scss";

const NOTIF_COT_STORAGE_KEY = "notif_cotizaciones_seen";
const NOTIF_DAYS_LOOKBACK = 30;

const allowedRegionIds = REGION_IDS;

const DAILY_EMOJIS = [
  "😉",
  "😊",
  "😃",
  "🙃",
  "😁",
  "🙂",
  "😎",
  "😄",
  "😌",
  "😏",
  "🤗",
  "👀",
  "🍀",
  "🙌",
  "🫶",
  "👊",
  "👋",
];

const getDailyEmoji = () => {
  const getEmojiForDay = (dayOffset) => {
    const date = new Date();
    date.setDate(date.getDate() - dayOffset);
    const seed = date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
    return Math.abs(Math.sin(seed) * 10000);
  };

  const recentEmojis = [];
  for (let i = 1; i <= 6; i++) {
    const idx = Math.floor(getEmojiForDay(i) % DAILY_EMOJIS.length);
    recentEmojis.push(DAILY_EMOJIS[idx]);
  }

  const available = DAILY_EMOJIS.filter((e) => !recentEmojis.includes(e));
  const todaySeed = getEmojiForDay(0);
  return available[Math.floor(todaySeed % available.length)];
};

export const Admin = () => {
  const { pais, session, nombre, rol, setRol, id } = useAuthContext();
  const canSwitchRole = ADMIN_IDS.includes(id);
  const canSwitchDemo = DEMO_SWITCH_IDS.includes(id);
  const demoRoles = [ROLES.COMERCIAL, ROLES.AGENCIA, ROLES.CLIENTE];
  const [propuestasCount, setPropuestasCount] = useState(0);
  // Badges desactivados — SII y Movimientos Banco integrados en Flujo de Caja

  useEffect(() => {
    if (!pais || !rol || pais === "Region") return;
    const rolesConBadge = [ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.BOARD];
    if (!rolesConBadge.includes(rol)) return;

    const otroPais = pais === "Chile" ? "Perú" : "Chile";
    const currentYear = new Date().getFullYear();

    (async () => {
      try {
        const [clientesOtroPais, salesOtroPais, cotizacionesLocales, usuarios] = await Promise.all([
          getClientes({ pais: otroPais }),
          getOportunidades({ pais: otroPais, ano: currentYear }),
          getAllCotizacionesPais(pais),
          getUsuariosConAgencia(pais),
        ]);

        // --- Pulso Regional (solo sales internas del otro país) ---
        const alerts = computePulsoRegional({
          miPais: pais,
          clientesOtroPais,
          salesOtroPais,
        });
        const authUid = session?.user?.id;
        const pulsoSeen = {};
        if (authUid) {
          const { data: seenRows } = await supabase
            .from("pulso_regional_seen")
            .select("empresa_key, seen_at")
            .eq("usuario_id", authUid);
          for (const row of seenRows || []) {
            pulsoSeen[row.empresa_key] = row.seen_at;
          }
        }
        const pulsoUnseen = alerts.filter((a) => {
          const key = `${(a.empresa || "").toLowerCase().trim()}|${a.paisOrigen}`;
          const seenAt = pulsoSeen[key];
          if (!seenAt) return true;
          return new Date(a.fecha) > new Date(seenAt);
        }).length;

        // --- Cotizaciones locales nuevas (últimos 30 días, borradores de agencia/cliente) ---
        const usuariosMap = {};
        for (const u of usuarios) usuariosMap[u.id] = u;

        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - NOTIF_DAYS_LOOKBACK);

        let cotSeen = {};
        try {
          cotSeen = JSON.parse(localStorage.getItem(NOTIF_COT_STORAGE_KEY) || "{}");
        } catch {
          /* ignore */
        }

        const cotUnseen = cotizacionesLocales.filter((c) => {
          if (!c.created_at || new Date(c.created_at) < cutoff) return false;
          if (c.estado === "ejecutada") return false;
          const rolCreador = usuariosMap[c.usuario_id]?.rol || "";
          if (rolCreador !== "agencia" && rolCreador !== "cliente") return false;
          const seenAt = cotSeen[c.id];
          if (!seenAt) return true;
          return new Date(c.created_at) > new Date(seenAt);
        }).length;

        setPropuestasCount(pulsoUnseen + cotUnseen);
      } catch {
        // silently ignore
      }
    })();
  }, [pais, rol]);

  // Badges de SII y Movimientos Banco desactivados — integrados en Flujo de Caja

  if (session === undefined) return <Loader fullPage />;

  const nombreUsuario = nombre || "usuario";

  const config = MENU_CONFIG[rol] || {
    utilitarias: () => [],
    ventas: () => [],
    administrativas: () => [],
    financieras: () => [],
  };

  const baseUtilitarias = [...UTILITARIAS_BASE, getIpsosItem(pais)];

  const utilitarias = config.utilitarias ? config.utilitarias({ allowedRegionIds, id, pais }) : baseUtilitarias;

  const ventasRaw = config.ventas({ allowedRegionIds, id, pais });
  const ventas = ventasRaw.map((link) =>
    link.path === "/admin/cotizaciones" && propuestasCount > 0 ? { ...link, badge: propuestasCount } : link
  );
  const administrativas = config.administrativas({ allowedRegionIds, id, pais });
  const financierasRaw = config.financieras({ allowedRegionIds, id, pais });
  const financieras = financierasRaw;

  const renderSection = (title, links) =>
    links.length > 0 && (
      <div className={styles.sectionWrapper}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{title}</h2>
          <div className={styles.botones}>
            {links.map((link) => (
              <div key={link.path} className={styles.linkWrapper}>
                {link.badge > 0 && <span className={styles.tileBadge}>{link.badge}</span>}
                {link.disabled ? (
                  <div className={`${styles.link} ${styles.linkDisabled}`}>
                    {link.icon}
                    <span>{link.text}</span>
                  </div>
                ) : link.external ? (
                  <a href={link.path} target="_blank" rel="noopener noreferrer" className={styles.link}>
                    {link.icon}
                    <span>{link.text}</span>
                  </a>
                ) : (
                  <Link to={link.path} className={styles.link}>
                    {link.icon}
                    <span>{link.text}</span>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );

  return (
    <div className={styles.Admin}>
      <Header
        leftContent={
          <p className={styles.welcomeText}>
            Hola {nombreUsuario}&nbsp; <span className={styles.welcomeEmoji}>{getDailyEmoji()}</span>
          </p>
        }
      >
        {(canSwitchRole || canSwitchDemo) && (
          <select
            className={styles.rolSelector}
            value={rol}
            onChange={async (e) => {
              const newRol = e.target.value;
              try {
                const { error } = await client.from("usuarios").update({ rol: newRol }).eq("id", id);
                if (error) throw error;
                setRol(newRol);
                toast.success(`Rol cambiado a ${newRol.toUpperCase()}`);
              } catch (err) {
                toast.error("Error al cambiar rol: " + err.message);
              }
            }}
          >
            {(canSwitchRole ? Object.values(ROLES) : demoRoles).map((r) => (
              <option key={r} value={r}>
                {r.toUpperCase()}
              </option>
            ))}
          </select>
        )}
        <button className={styles.logoutButton} onClick={logout}>
          LOGOUT
        </button>
      </Header>

      <div className={styles.sectionsContainer}>
        {renderSection("UTILITARIAS", utilitarias)}
        {renderSection(rol === ROLES.AGENCIA || rol === ROLES.CLIENTE ? "COMPRAS" : "VENTAS", ventas)}
        {renderSection("ADMINISTRACIÓN", administrativas)}
        {renderSection("FINANCIERAS", financieras)}
      </div>
    </div>
  );
};
